<template>
  <div>
    <h3 v-if="records.length && headline" class="pt-8">{{ headline }}</h3>
    <DataTable
      v-if="records.length > 0"
      :data-key="`${component}TrackRecord`"
      :columns="columns"
      :data="records"
      :isLoadingFinished="isLoadingFinished"
      :reorderableRows="false"
      :tableActions="false"
      :rowClass="() => 'elevated-row'"
      :showPrimaryToolbar="false"
      :showSecondaryToolbarButtons="false"
      :showSecondaryToolbar="false"
      :onRowClick="() => {}"
      :onRowDblClick="() => {}"
      :checkboxMultiselect="false"
    >
      <template #body-changes="{ row }">
        <div v-if="!!row['changes'].meta('rawvalue')">
          <div
            v-for="change in row['changes'].meta('rawvalue')"
            :key="change.fieldName"
            class="change"
          >
            <span class="fieldName">{{ t(`labels.${change.fieldName}`) }}</span>
            <span class="oldValue">{{ change.oldValue }}</span>
            <span class="newValue">{{ change.newValue }}</span>
          </div>
        </div>
        <div v-else>
          &nbsp;
          <!-- otherwise empty array appears-->
        </div>
      </template>
    </DataTable>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { useListView } from '@/composables/useListView';
import type { EntityHistoryV1Dto } from '@/openapi';
import { useTrackRecording } from '@/track-recording/hooks/useTrackRecording';

import { columns } from './configs/trackRecoordingColumnConfig';

const emit = defineEmits<{
  (event: 'update:records', records: any[]): void;
}>();

// Routing
const route = useRoute();
const { component } = route.params;

const props = defineProps<{
  id: string | number;
  headline?: string;
}>();

const { t } = useI18n();

const { fetchRecords } = useTrackRecording();

const records = ref<EntityHistoryV1Dto[]>([]);
const headline = ref<string>(props.headline ?? t('text.trackRecords'));

const loadRecords = async () => {
  if (!props.id) return;
  try {
    records.value = ((await fetchRecords(String(props.id))) ?? []).map(
      (record: EntityHistoryV1Dto) => {
        const flatData: any = {
          ...record,
          editorName: record.data.userName,
          changedAt: record.createdAt,
          ...record.data,
        };
        const { data: _data, userName: _userName, ...tableData } = flatData;
        return tableData;
      },
    );
  } catch (error) {
    console.error(`Error fetching track records for ID: ${props.id}`, error);
    records.value = [];
  }
};

const { isLoadingFinished } = useListView({ fetchData: loadRecords });

watch(
  () => props.id,
  async (newId, oldId) => {
    if (newId !== oldId) {
      await loadRecords();
    }
  },
  { immediate: true },
);

watch(
  () => records.value,
  (newRecords) => {
    emit('update:records', newRecords);
  },
);
</script>

<style scoped lang="css">
@reference "tailwindcss";
@reference "tailwindcss/utilities";
@layer app {
  .fieldName {
    flex: 1;
  }
  .oldValue,
  .newValue {
    flex: 2;
    text-align: left;
  }

  .change {
    @apply mb-2 flex;
    align-items: flex-start;
  }

  .change + .change {
    margin-top: 0.5rem;
  }

  .fieldName {
    @apply w-40;
    font-weight: bold;
    margin-right: 1rem;
  }

  .oldValue {
    text-decoration: line-through;
    margin-right: 1rem;
  }

  .newValue {
    @apply font-normal;
  }

  /* :deep() */
  .no-data {
    display: none;
  }
}
</style>
